/**
 * DelegheePage v109 — wizard SMART per le Deleghe (DPR 445/2000).
 *
 * Flusso ottimizzato:
 *  1. Selezioni l'AZIENDA dal dropdown → autopopolano:
 *     - dati Società (denominazione, P.IVA, sede legale, città, provincia)
 *     - dati Dichiarante (legale rappresentante della azienda)
 *  2. Dati DELEGATO presi automaticamente dalla tua INTESTAZIONE STUDIO (Profilo)
 *     incluso titolo, P.IVA, CF. Il timbro/firma sono inseriti automaticamente
 *     nel .docx generato.
 *  3. Compili solo: Scopo, Ufficio destinatario, Validità, Data firma
 *  4. Click "Genera Word" → scarica il documento pronto e firmato.
 */
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FileText, Trash2, ArrowLeft, Loader2, Save, Building2, UserCheck, Sparkles, AlertTriangle, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const empty = {
  dichiarante_nome: '', dichiarante_cognome: '', dichiarante_nato_a: '', dichiarante_nato_il: '',
  dichiarante_codice_fiscale: '', dichiarante_qualifica: 'Legale Rappresentante',
  societa_denominazione: '', societa_cf_piva: '',
  societa_sede_indirizzo: '', societa_sede_cap: '', societa_sede_citta: '', societa_sede_provincia: '',
  attivita_presente: false, attivita_comune: '', attivita_indirizzo: '',
  attivita_foglio: '', attivita_particella: '', attivita_categoria_catastale: '',
  delegato_titolo: 'Sig.', delegato_nome: '', delegato_cognome: '', delegato_nato_a: '', delegato_nato_il: '',
  delegato_codice_fiscale: '', delegato_ordine_provincia: '', delegato_ordine_numero: '', delegato_ordine_sezione: '',
  scopo_key: 'accesso_atti', scopo_descrizione: '',
  ufficio_nome: 'SUAP', ufficio_comune: '', ufficio_provincia: '',
  valida_fino_a: '', luogo_firma: '', data_firma: '',
  stato: 'bozza', note: '',
};

export default function DelegheePage() {
  const [list, setList] = useState([]);
  const [scopi, setScopi] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [intestazione, setIntestazione] = useState({});
  const [mode, setMode] = useState('list');
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [selectedAziendaId, setSelectedAziendaId] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: s }, { data: az }, intRes] = await Promise.all([
        api.get('/studio/deleghe'),
        api.get('/studio/deleghe/scopi-preset'),
        api.get('/aziende'),
        api.get('/users/me/intestazione').catch(() => ({ data: {} })),
      ]);
      setList(l);
      setScopi(s.scopi);
      setAziende(az || []);
      setIntestazione(intRes.data || {});
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const intestazioneCompleta = useMemo(() => {
    return !!(intestazione?.studio_nome || intestazione?.studio_cf || intestazione?.studio_qualifica);
  }, [intestazione]);

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setSelectedAziendaId('');
    setWarnings([]);
    setShowAdvanced(false);
    setMode('form');
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/studio/deleghe/${id}`);
      setForm({ ...empty, ...data });
      setEditingId(id);
      setSelectedAziendaId(data.azienda_id || '');
      setMode('form');
    } catch (e) { toast.error('Errore: ' + (e?.response?.data?.detail || e.message)); }
  };

  const onSelectAzienda = async (azId) => {
    setSelectedAziendaId(azId);
    if (!azId) return;
    try {
      const { data } = await api.get(`/studio/deleghe/prefill/${azId}`);
      setWarnings(data._warnings || []);
      const { _warnings, ...prefill } = data;
      setForm(f => ({ ...f, ...prefill }));
      toast.success('Dati azienda caricati');
    } catch (e) {
      toast.error('Errore prefill: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const save = async (download = false) => {
    if (!form.societa_denominazione) { toast.error('Seleziona prima un\'Azienda dal dropdown'); return; }
    if (!form.delegato_nome || !form.delegato_cognome) {
      toast.error('Manca il delegato — completa la tua Intestazione Studio in Profilo');
      return;
    }
    setSaving(true);
    try {
      let res;
      const payload = { ...form, azienda_id: selectedAziendaId };
      if (editingId) res = await api.put(`/studio/deleghe/${editingId}`, payload);
      else res = await api.post('/studio/deleghe', payload);
      toast.success(editingId ? 'Delega aggiornata' : 'Delega creata');
      const id = res.data.id;
      setEditingId(id);
      if (download) await downloadDocx(id);
      else { await load(); setMode('list'); }
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const downloadDocx = async (id) => {
    try {
      const resp = await api.get(`/studio/deleghe/${id}/docx`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Delega_${form.delegato_cognome || 'delegato'}_${form.ufficio_nome || 'PA'}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Word scaricato');
    } catch (e) { toast.error('Errore download: ' + (e?.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questa delega?')) return;
    try { await api.delete(`/studio/deleghe/${id}`); toast.success('Eliminata'); load(); }
    catch (e) { toast.error('Errore: ' + (e?.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  // ============= LIST VIEW =============
  if (mode === 'list') {
    return (
      <div className="p-4 max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="deleghe-title">
              <FileText className="w-7 h-7 text-indigo-600"/> Deleghe (DPR 445/2000)
            </h1>
            <p className="text-sm text-slate-600">
              Dichiarazione sostitutiva di atto di notorietà per accesso atti, firma digitale, presentazione istanze SUAP/SCIA/CILA.
            </p>
          </div>
          <Button onClick={openNew} data-testid="new-delega-btn" className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-1" /> Nuova Delega
          </Button>
        </div>

        {!intestazioneCompleta && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded text-sm">
            <strong className="text-amber-900">⚠ Intestazione studio incompleta</strong>
            <p className="text-amber-800 mt-1">
              Vai in <strong>Profilo → Intestazione Studio</strong> e compila nome, qualifica, indirizzo, P.IVA, CF + carica
              firma e timbro. Senza questi dati non potremo precompilare il delegato e il timbro nelle deleghe.
            </p>
          </div>
        )}

        {list.length === 0 ? (
          <Card><CardContent className="pt-8 pb-8 text-center text-slate-600">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p>Nessuna delega creata.</p>
            <p className="text-xs mt-1">Clicca "Nuova Delega" per iniziare.</p>
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b"><tr className="text-left">
                <th className="p-2 text-xs">Delegato</th><th className="p-2 text-xs">Società</th>
                <th className="p-2 text-xs">Ufficio</th><th className="p-2 text-xs">Scopo</th>
                <th className="p-2 text-xs">Validità</th><th className="p-2 text-right text-xs">Azioni</th>
              </tr></thead>
              <tbody>
                {list.map(d => (
                  <tr key={d.id} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-medium">{d.delegato_titolo} {d.delegato_nome} {d.delegato_cognome}</td>
                    <td className="p-2 text-slate-600">{d.societa_denominazione || '—'}</td>
                    <td className="p-2 text-slate-600">{d.ufficio_nome} — {d.ufficio_comune}</td>
                    <td className="p-2 text-xs">{scopi.find(s => s.key === d.scopo_key)?.label || d.scopo_key}</td>
                    <td className="p-2 text-xs">{d.valida_fino_a || 'senza scadenza'}</td>
                    <td className="p-2 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(d.id)} data-testid={`edit-${d.id}`}>Apri</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadDocx(d.id)} title="Scarica Word"><FileText className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(d.id)}><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </div>
    );
  }

  // ============= SMART FORM VIEW =============
  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4 pb-32">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setMode('list')}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600"/>
          {editingId ? 'Modifica Delega' : 'Nuova Delega — Compilazione Smart'}
        </h1>
      </div>

      <p className="text-sm text-slate-600 bg-indigo-50 border border-indigo-200 rounded p-3">
        <strong>💡 Come funziona:</strong> Seleziona un'azienda dal menu sotto → il sistema precompila <em>società</em>, <em>dichiarante</em> (legale rappresentante)
        e <em>delegato</em> (te stesso, dalla tua Intestazione Studio). Compili solo lo scopo e l'ufficio destinatario.
      </p>

      {/* STEP 1 — AZIENDA */}
      <Card className="border-indigo-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600"/> 1. Seleziona l'Azienda
          </CardTitle>
          <CardDescription>I dati di società e legale rappresentante saranno precompilati automaticamente.</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedAziendaId} onValueChange={onSelectAzienda}>
            <SelectTrigger data-testid="azienda-select" className="w-full">
              <SelectValue placeholder="Cerca / scegli un'azienda…"/>
            </SelectTrigger>
            <SelectContent>
              {aziende.length === 0 && <div className="p-2 text-xs text-muted-foreground">Nessuna azienda disponibile</div>}
              {aziende.map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {a.denominazione} {a.partita_iva ? `· P.IVA ${a.partita_iva}` : ''} {a.citta ? `· ${a.citta}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {warnings.length > 0 && (
            <div className="mt-3 space-y-1">
              {warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5"/>
                  <span className="text-amber-900">{w}</span>
                </div>
              ))}
            </div>
          )}

          {selectedAziendaId && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm bg-slate-50 rounded p-3">
              <div><span className="text-slate-500">Società:</span> <strong>{form.societa_denominazione || '—'}</strong></div>
              <div><span className="text-slate-500">P.IVA / CF:</span> <strong>{form.societa_cf_piva || '—'}</strong></div>
              <div className="md:col-span-2"><span className="text-slate-500">Sede legale:</span> <strong>{form.societa_sede_indirizzo} {form.societa_sede_cap} {form.societa_sede_citta} ({form.societa_sede_provincia})</strong></div>
              <div className="md:col-span-2"><span className="text-slate-500">Legale Rappresentante:</span>
                <strong className={form.dichiarante_nome ? '' : 'text-amber-700'}>
                  {form.dichiarante_nome || form.dichiarante_cognome
                    ? ` ${form.dichiarante_qualifica} ${form.dichiarante_nome} ${form.dichiarante_cognome}`
                    : ' ⚠ da inserire (Modifica anagrafica azienda)'}
                </strong>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* STEP 2 — DELEGATO (auto da intestazione) */}
      {selectedAziendaId && (
        <Card className="border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600"/> 2. Delegato (te stesso)
            </CardTitle>
            <CardDescription>
              I dati arrivano dalla tua <strong>Intestazione Studio</strong> (Profilo). Modificali lì se cambiano in modo permanente.
              <br/>Firma e timbro saranno inseriti automaticamente nel Word generato.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {form.delegato_nome || form.delegato_cognome ? (
              <div className="bg-emerald-50 rounded p-3">
                <p className="text-sm">
                  <strong>{form.delegato_titolo} {form.delegato_nome} {form.delegato_cognome}</strong>
                  {form.delegato_codice_fiscale && <span className="text-slate-600"> · CF: {form.delegato_codice_fiscale}</span>}
                </p>
                {(intestazione.studio_qualifica || intestazione.studio_indirizzo) && (
                  <p className="text-xs text-slate-600 mt-1">
                    {intestazione.studio_qualifica}{intestazione.studio_indirizzo && ` · ${intestazione.studio_indirizzo}`}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                <strong className="text-red-900">Manca la tua Intestazione Studio.</strong>
                <p className="text-red-800 mt-1">
                  Vai in <a href="/profile" className="underline font-medium">Profilo → Intestazione Studio</a>,
                  compila nome, qualifica, P.IVA/CF e carica firma+timbro, poi torna qui.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 3 — SCOPO */}
      {selectedAziendaId && (
        <Card className="border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">3. Per cosa ti deleghiamo?</CardTitle>
            <CardDescription>Scegli lo scopo e l'ufficio destinatario della delega.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Scopo *</Label>
              <Select value={form.scopo_key} onValueChange={v => {
                upd('scopo_key', v);
                const s = scopi.find(x => x.key === v);
                if (s && s.uffici_suggeriti?.length) upd('ufficio_nome', s.uffici_suggeriti[0]);
              }}>
                <SelectTrigger data-testid="scopo-select"><SelectValue/></SelectTrigger>
                <SelectContent>{scopi.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.scopo_key === 'altro' && (
              <div>
                <Label className="text-xs">Specifica scopo</Label>
                <Textarea
                  value={form.scopo_descrizione}
                  onChange={e => upd('scopo_descrizione', e.target.value)}
                  rows={2}
                  placeholder="es. presentare richiesta di variante in deroga al PRG, ricevere copie ecc."
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Ufficio / Sportello</Label>
                <Input
                  value={form.ufficio_nome}
                  onChange={e => upd('ufficio_nome', e.target.value)}
                  placeholder="SUAP / Ufficio Tecnico"
                  list="uffici-sugg"
                  data-testid="ufficio-nome"
                />
                <datalist id="uffici-sugg">
                  {(scopi.find(s => s.key === form.scopo_key)?.uffici_suggeriti || []).map(u => <option key={u} value={u}/>)}
                </datalist>
              </div>
              <div>
                <Label className="text-xs">Comune</Label>
                <Input value={form.ufficio_comune} onChange={e => upd('ufficio_comune', e.target.value)} data-testid="ufficio-comune"/>
              </div>
              <div>
                <Label className="text-xs">Prov.</Label>
                <Input value={form.ufficio_provincia} onChange={e => upd('ufficio_provincia', e.target.value.toUpperCase())} maxLength={2}/>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Validità (gg/mm/aaaa)</Label>
                <Input value={form.valida_fino_a} onChange={e => upd('valida_fino_a', e.target.value)} placeholder="senza scadenza se vuoto"/>
              </div>
              <div>
                <Label className="text-xs">Luogo firma</Label>
                <Input value={form.luogo_firma} onChange={e => upd('luogo_firma', e.target.value)}/>
              </div>
              <div>
                <Label className="text-xs">Data firma</Label>
                <Input value={form.data_firma} onChange={e => upd('data_firma', e.target.value)}/>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 — Avanzato (catasto, ordine professionale) */}
      {selectedAziendaId && (
        <button
          type="button"
          onClick={() => setShowAdvanced(s => !s)}
          className="w-full text-left p-3 border-2 border-dashed border-slate-300 rounded text-sm text-slate-600 hover:border-slate-400 flex items-center justify-between"
          data-testid="toggle-avanzato"
        >
          <span><ChevronRight className={`inline w-4 h-4 mr-1 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}/>Opzioni avanzate (dati catastali, iscrizione ordine professionale)</span>
        </button>
      )}

      {showAdvanced && selectedAziendaId && (
        <Card>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4">
            <div className="md:col-span-3 flex items-center gap-2">
              <input type="checkbox" checked={form.attivita_presente} onChange={e => upd('attivita_presente', e.target.checked)} data-testid="attivita-toggle"/>
              <Label className="text-sm">Aggiungi dettagli attività / dati catastali</Label>
            </div>
            {form.attivita_presente && (
              <>
                <div className="md:col-span-2"><Label className="text-xs">Indirizzo attività</Label><Input value={form.attivita_indirizzo} onChange={e => upd('attivita_indirizzo', e.target.value)}/></div>
                <div><Label className="text-xs">Comune attività</Label><Input value={form.attivita_comune} onChange={e => upd('attivita_comune', e.target.value)}/></div>
                <div><Label className="text-xs">Foglio</Label><Input value={form.attivita_foglio} onChange={e => upd('attivita_foglio', e.target.value)}/></div>
                <div><Label className="text-xs">Particella</Label><Input value={form.attivita_particella} onChange={e => upd('attivita_particella', e.target.value)}/></div>
                <div><Label className="text-xs">Categoria catastale</Label><Input value={form.attivita_categoria_catastale} onChange={e => upd('attivita_categoria_catastale', e.target.value)} placeholder="es. D/8"/></div>
              </>
            )}
            <div className="md:col-span-3 mt-2 p-2 bg-slate-50 rounded">
              <p className="text-xs font-medium mb-2 text-slate-700">Iscrizione Ordine professionale del delegato (se applicabile)</p>
              <div className="grid grid-cols-3 gap-2">
                <Input value={form.delegato_ordine_provincia} onChange={e => upd('delegato_ordine_provincia', e.target.value)} placeholder="Provincia ordine"/>
                <Input value={form.delegato_ordine_numero} onChange={e => upd('delegato_ordine_numero', e.target.value)} placeholder="N. iscrizione"/>
                <Input value={form.delegato_ordine_sezione} onChange={e => upd('delegato_ordine_sezione', e.target.value)} placeholder="Sezione (A/B)"/>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AZIONI sticky */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t shadow-lg p-3 flex items-center justify-end gap-2 z-20">
        <Button variant="outline" onClick={() => save(false)} disabled={saving || !selectedAziendaId} data-testid="save-delega">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Salva
        </Button>
        <Button onClick={() => save(true)} disabled={saving || !selectedAziendaId} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-and-download">
          <FileText className="w-4 h-4 mr-1" /> Salva e Scarica Word
        </Button>
      </div>
    </div>
  );
}
